// Turns the outcome of an invite into the sentence shown in the confirmation
// toast, plus the tone that decides which flag colour it uses.
//
// Four buckets. `invalid` never reaches the backend - those addresses failed
// validation in the modal - and the other three come back from the endpoint.
//
// Two forms of the "sent" clause, chosen by outcome rather than by count:
//   everything sent  -> "Invite sent to jane@acme.com." / "Invites sent to 5 team members."
//   anything skipped -> "5 invites sent." so the next clause reads on from it
//
// Strings are reproduced from the design exactly, including the missing full
// stop on the single-member sentence.

const asList = (value) => (Array.isArray(value) ? value : []);

const sentClause = (sent, hasSkipped) => {
  if (sent.length === 0) {
    return null;
  }

  if (!hasSkipped) {
    return sent.length === 1
      ? `Invite sent to ${sent[0]}.`
      : `Invites sent to ${sent.length} team members.`;
  }

  return sent.length === 1 ? "1 invite sent." : `${sent.length} invites sent.`;
};

// Not counted - the count is already in the sent clause, and the addresses
// themselves are still on screen in the field.
const invalidClause = (invalid) =>
  invalid.length > 0 ? "Fix the addresses below to send the rest." : null;

// When nothing got through at all, the invalid addresses are the whole story.
const nothingSentClause = (invalid) =>
  invalid.length === 1
    ? "Couldn't send 1 invite. Check the email address and try again."
    : `Couldn't send ${invalid.length} invites. Check the email addresses and try again.`;

const alreadyInvitedClause = (alreadyInvited) => {
  if (alreadyInvited.length === 0) {
    return null;
  }

  return alreadyInvited.length === 1
    ? `${alreadyInvited[0]} has already been invited. Resend invite?`
    : `${alreadyInvited.length} people have already been invited.`;
};

const alreadyMemberClause = (alreadyMember) => {
  if (alreadyMember.length === 0) {
    return null;
  }

  return alreadyMember.length === 1
    ? `${alreadyMember[0]} is already on your team`
    : `${alreadyMember.length} people are already on your team.`;
};

// Red if nothing got through because of the user's typing, green if the only
// problem was their typing but some still went, neutral if the backend skipped
// anything. The two design frames disagree about the mixed case; neutral wins
// there because a backend skip is present.
const toneFor = ({ sent, invalid, backendSkipped }) => {
  if (backendSkipped) {
    return "neutral";
  }
  if (sent === 0 && invalid > 0) {
    return "error";
  }
  return "success";
};

export const buildInviteResultMessage = ({
  sent,
  alreadyInvited,
  alreadyMember,
  invalid,
} = {}) => {
  const sentList = asList(sent);
  const invitedList = asList(alreadyInvited);
  const memberList = asList(alreadyMember);
  const invalidList = asList(invalid);

  const backendSkipped = invitedList.length > 0 || memberList.length > 0;
  const hasSkipped = backendSkipped || invalidList.length > 0;

  const clauses =
    sentList.length === 0 && invalidList.length > 0 && !backendSkipped
      ? [nothingSentClause(invalidList)]
      : [
          sentClause(sentList, hasSkipped),
          invalidClause(invalidList),
          alreadyInvitedClause(invitedList),
          alreadyMemberClause(memberList),
        ].filter(Boolean);

  return {
    // The fallback covers a response with no buckets at all, which is what an
    // older backend returns.
    message: clauses.length > 0 ? clauses.join(" ") : "Invite sent.",
    resendEmails: invitedList,
    tone: toneFor({
      sent: sentList.length,
      invalid: invalidList.length,
      backendSkipped,
    }),
  };
};

export default buildInviteResultMessage;
